import type { ValidationResult } from "@unique-wool/specs-validation";
import { validateDecorations } from "@unique-wool/specs-validation";
import { ObservableModelRoot } from "./observable";
import type {
  DecorationSpecsModelSnapshot,
  DecorationSpecsModelValidationContext,
  SpecsCatalog,
} from "./types";
import { DecorationAssetModel } from "./decorations/DecorationAssetModel";
import { DecorationCreditsModel } from "./decorations/DecorationCreditsModel";
import { DecorationDisplayModel } from "./decorations/DecorationDisplayModel";
import { DecorationTechpackModel } from "./decorations/DecorationTechpackModel";
import { LocalizedTextModel } from "./decorations/LocalizedTextModel";
import { SpecsDecorationModel } from "./decorations/SpecsDecorationModel";

export class SpecsDecorationsModel extends ObservableModelRoot {
  decoration!: SpecsDecorationModel;
  private validationContext!: DecorationSpecsModelValidationContext;

  constructor() {
    super();
    this.defineObservableValue("decoration", new SpecsDecorationModel());
    this.decoration.factory = "AT";
    this.decoration.displayName = new LocalizedTextModel(
      "decoration displayName"
    );
    this.decoration.display = new DecorationDisplayModel();
    this.decoration.group = "draft";
    this.decoration.assetProcessing = "none";
    this.decoration.fileTypes = [{ name: "draft", extensions: [] }];
    this.decoration.asset = new DecorationAssetModel();
    this.decoration.credits = new DecorationCreditsModel();
    this.decoration.techpack = new DecorationTechpackModel();
    this.validationContext = {};
  }

  restore(snapshot: DecorationSpecsModelSnapshot) {
    this.decoration.restore(snapshot);
    return this;
  }

  save(): DecorationSpecsModelSnapshot {
    return this.decoration.save();
  }

  setValidationContext(context: DecorationSpecsModelValidationContext = {}) {
    this.validationContext = context;
    return this;
  }

  buildValidationSpecs() {
    return {
      families: [],
      fabrics: [],
      decorations: [this.save()],
      products: [],
    };
  }

  async validate(): Promise<ValidationResult> {
    const issues = await validateDecorations(this.buildValidationSpecs());
    return {
      valid: issues.length === 0,
      issues,
    };
  }

  static fromCatalog(specs: SpecsCatalog, specName: string) {
    const decorations = Array.isArray(specs.decorations)
      ? specs.decorations
      : null;
    const decoration = decorations?.find((candidate) => {
      const candidateId =
        typeof candidate?.id === "string" ? candidate.id.trim() : "";
      const candidateName = candidate?.name;
      const lookupValue = candidateId.length > 0 ? candidate.id : candidateName;

      return lookupValue === specName;
    });

    if (!decoration) {
      return null;
    }

    return new SpecsDecorationsModel().restore(
      decoration as DecorationSpecsModelSnapshot
    );
  }
}
